import React, { useState } from 'react';
import styled from 'styled-components';

// Styled Components
const CheckoutContainer = styled.div`
  background-color: #0a0a0a;
  border-radius: 12px;
  padding: 24px;
  color: white;
  min-height: 600px;
  max-height: 80vh;
  overflow-y: auto;
`;

const CheckoutHeader = styled.div`
  text-align: center;
  margin-bottom: 24px;
`;

const CheckoutTitle = styled.h2`
  color: #D4AF37;
  margin: 0 0 8px 0;
  font-size: 28px;
  font-weight: bold;
`;

const CheckoutSubtitle = styled.p`
  color: #ccc;
  margin: 0;
  font-size: 16px;
`;

const CheckoutSteps = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 32px;
`;

const Step = styled.div`
  display: flex;
  align-items: center;
  margin: 0 16px;
  
  &:not(:last-child)::after {
    content: '';
    width: 40px;
    height: 2px;
    background: ${props => props.$completed ? '#D4AF37' : '#4b5563'};
    margin-left: 16px;
  }
`;

const StepNumber = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: ${props => props.$active ? '#D4AF37' : props.$completed ? '#22c55e' : '#4b5563'};
  color: ${props => props.$active || props.$completed ? '#0a0a0a' : '#9ca3af'};
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  font-size: 14px;
  margin-right: 8px;
`;

const StepLabel = styled.span`
  color: ${props => props.$active ? '#D4AF37' : props.$completed ? '#22c55e' : '#9ca3af'};
  font-size: 14px;
  font-weight: ${props => props.$active ? 'bold' : '500'};
`;

const FormSection = styled.div`
  margin-bottom: 32px;
`;

const SectionTitle = styled.h3`
  color: #D4AF37;
  margin: 0 0 16px 0;
  font-size: 20px;
  font-weight: bold;
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const FormField = styled.div`
  display: flex;
  flex-direction: column;
`;

const FormLabel = styled.label`
  color: #d1d5db;
  margin-bottom: 8px;
  font-size: 14px;
  font-weight: 500;
`;

const FormInput = styled.input`
  padding: 12px 16px;
  background: #1f2937;
  border: 2px solid #4b5563;
  border-radius: 8px;
  color: white;
  font-size: 16px;
  transition: all 0.3s ease;
  
  &:focus {
    outline: none;
    border-color: #D4AF37;
    box-shadow: 0 0 0 3px rgba(212, 175, 55, 0.1);
  }
  
  &::placeholder {
    color: #6b7280;
  }
`;

const FormTextarea = styled.textarea`
  padding: 12px 16px;
  background: #1f2937;
  border: 2px solid #4b5563;
  border-radius: 8px;
  color: white;
  font-size: 16px;
  resize: vertical;
  min-height: 80px;
  transition: all 0.3s ease;
  
  &:focus {
    outline: none;
    border-color: #D4AF37;
    box-shadow: 0 0 0 3px rgba(212, 175, 55, 0.1);
  }
  
  &::placeholder {
    color: #6b7280;
  }
`;

const FullWidthField = styled(FormField)`
  grid-column: 1 / -1;
`;

const OrderSummary = styled.div`
  background: linear-gradient(135deg, #1f2937 0%, #374151 100%);
  border-radius: 16px;
  padding: 24px;
  margin-bottom: 24px;
`;

const SummaryTitle = styled.h4`
  color: #D4AF37;
  margin: 0 0 16px 0;
  font-size: 18px;
  font-weight: bold;
`;

const SummaryItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
  
  &:last-child {
    margin-bottom: 0;
    padding-top: 16px;
    border-top: 2px solid #4b5563;
  }
`;

const SummaryLabel = styled.span`
  color: #d1d5db;
  font-size: 14px;
`;

const SummaryValue = styled.span`
  color: ${props => props.$highlight ? '#D4AF37' : '#22c55e'};
  font-size: ${props => props.$highlight ? '20px' : '14px'};
  font-weight: ${props => props.$highlight ? 'bold' : '500'};
`;

const PaymentSection = styled.div`
  margin-bottom: 32px;
`;

const PaymentOptions = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
  margin-bottom: 24px;
`;

const PaymentOption = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px;
  background: ${props => props.$selected ? 'rgba(212, 175, 55, 0.1)' : '#1f2937'};
  border: 2px solid ${props => props.$selected ? '#D4AF37' : '#4b5563'};
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    border-color: #D4AF37;
    transform: translateY(-2px);
  }
`;

const PaymentIcon = styled.div`
  font-size: 24px;
`;

const PaymentLabel = styled.div`
  color: ${props => props.$selected ? '#D4AF37' : '#d1d5db'};
  font-weight: ${props => props.$selected ? 'bold' : '500'};
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 12px;
  justify-content: center;
`;

const BackButton = styled.button`
  padding: 12px 24px;
  background: transparent;
  color: #9ca3af;
  border: 2px solid #4b5563;
  border-radius: 25px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.3s ease;
  
  &:hover {
    border-color: #6b7280;
    color: #d1d5db;
  }
`;

const NextButton = styled.button`
  padding: 12px 24px;
  background: linear-gradient(135deg, #D4AF37 0%, #fbbf24 100%);
  color: #0a0a0a;
  border: none;
  border-radius: 25px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 20px rgba(212, 175, 55, 0.3);
  }
  
  &:disabled {
    background: #6b7280;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`;

const PlaceOrderButton = styled.button`
  padding: 16px 32px;
  background: linear-gradient(135deg, #22c55e 0%, #16a34a 100%);
  color: white;
  border: none;
  border-radius: 25px;
  cursor: pointer;
  font-size: 16px;
  font-weight: 600;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 20px rgba(34, 197, 94, 0.3);
  }
  
  &:disabled {
    background: #6b7280;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`;

const CheckoutFlow = ({ cart, onClose, onSuccess }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    specialInstructions: '',
    paymentMethod: 'card'
  });
  const [isProcessing, setIsProcessing] = useState(false);

  const steps = [
    { number: 1, label: 'Contact Info' },
    { number: 2, label: 'Payment' },
    { number: 3, label: 'Review' }
  ];

  const calculateSubtotal = () => {
    return cart.reduce((total, item) => total + (item.price * item.quantity), 0);
  };

  const calculateTax = () => {
    return calculateSubtotal() * 0.08; // 8% tax
  };

  const calculateTotal = () => {
    return calculateSubtotal() + calculateTax();
  };

  const formatPrice = (price) => {
    return `$${price.toFixed(2)}`;
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleNext = () => {
    if (currentStep < 3) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const isFormValid = () => {
    const requiredFields = ['firstName', 'lastName', 'email', 'phone'];
    return requiredFields.every(field => formData[field].trim() !== '');
  };

  const handlePlaceOrder = async () => {
    if (!isFormValid()) {
      alert('Please fill in all required fields');
      return;
    }

    setIsProcessing(true);
    
    try {
      // Simulate order processing
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      const orderData = {
        orderId: `ORDER_${Date.now()}`,
        items: cart,
        customerInfo: formData,
        total: calculateTotal(),
        status: 'confirmed',
        timestamp: new Date().toISOString()
      };
      
      console.log('🎉 Order placed successfully:', orderData);
      onSuccess(orderData);
      
    } catch (error) {
      console.error('❌ Order failed:', error);
      alert('Order failed. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  const renderContactInfo = () => (
    <FormSection>
      <SectionTitle>📞 Contact Information</SectionTitle>
      <FormGrid>
        <FormField>
          <FormLabel>First Name *</FormLabel>
          <FormInput
            type="text"
            value={formData.firstName}
            onChange={(e) => handleInputChange('firstName', e.target.value)}
            placeholder="Enter your first name"
            required
          />
        </FormField>
        
        <FormField>
          <FormLabel>Last Name *</FormLabel>
          <FormInput
            type="text"
            value={formData.lastName}
            onChange={(e) => handleInputChange('lastName', e.target.value)}
            placeholder="Enter your last name"
            required
          />
        </FormField>
        
        <FormField>
          <FormLabel>Email *</FormLabel>
          <FormInput
            type="email"
            value={formData.email}
            onChange={(e) => handleInputChange('email', e.target.value)}
            placeholder="Enter your email"
            required
          />
        </FormField>
        
        <FormField>
          <FormLabel>Phone *</FormLabel>
          <FormInput
            type="tel"
            value={formData.phone}
            onChange={(e) => handleInputChange('phone', e.target.value)}
            placeholder="Enter your phone number"
            required
          />
        </FormField>
        
        <FullWidthField>
          <FormLabel>Address</FormLabel>
          <FormInput
            type="text"
            value={formData.address}
            onChange={(e) => handleInputChange('address', e.target.value)}
            placeholder="Enter your address"
          />
        </FullWidthField>
        
        <FormField>
          <FormLabel>City</FormLabel>
          <FormInput
            type="text"
            value={formData.city}
            onChange={(e) => handleInputChange('city', e.target.value)}
            placeholder="Enter your city"
          />
        </FormField>
        
        <FormField>
          <FormLabel>State</FormLabel>
          <FormInput
            type="text"
            value={formData.state}
            onChange={(e) => handleInputChange('state', e.target.value)}
            placeholder="Enter your state"
          />
        </FormField>
        
        <FormField>
          <FormLabel>ZIP Code</FormLabel>
          <FormInput
            type="text"
            value={formData.zipCode}
            onChange={(e) => handleInputChange('zipCode', e.target.value)}
            placeholder="Enter ZIP code"
          />
        </FormField>
        
        <FullWidthField>
          <FormLabel>Special Instructions</FormLabel>
          <FormTextarea
            value={formData.specialInstructions}
            onChange={(e) => handleInputChange('specialInstructions', e.target.value)}
            placeholder="Any special instructions for your order?"
          />
        </FullWidthField>
      </FormGrid>
    </FormSection>
  );

  const renderPayment = () => (
    <PaymentSection>
      <SectionTitle>💳 Payment Method</SectionTitle>
      <PaymentOptions>
        <PaymentOption
          $selected={formData.paymentMethod === 'card'}
          onClick={() => handleInputChange('paymentMethod', 'card')}
        >
          <PaymentIcon>💳</PaymentIcon>
          <PaymentLabel $selected={formData.paymentMethod === 'card'}>
            Credit/Debit Card
          </PaymentLabel>
        </PaymentOption>
        
        <PaymentOption
          $selected={formData.paymentMethod === 'cash'}
          onClick={() => handleInputChange('paymentMethod', 'cash')}
        >
          <PaymentIcon>💵</PaymentIcon>
          <PaymentLabel $selected={formData.paymentMethod === 'cash'}>
            Cash on Delivery
          </PaymentLabel>
        </PaymentOption>
        
        <PaymentOption
          $selected={formData.paymentMethod === 'digital'}
          onClick={() => handleInputChange('paymentMethod', 'digital')}
        >
          <PaymentIcon>📱</PaymentIcon>
          <PaymentLabel $selected={formData.paymentMethod === 'digital'}>
            Digital Wallet
          </PaymentLabel>
        </PaymentOption>
      </PaymentOptions>
      
      {formData.paymentMethod === 'card' && (
        <FormGrid>
          <FullWidthField>
            <FormLabel>Card Number</FormLabel>
            <FormInput
              type="text"
              placeholder="1234 5678 9012 3456"
              maxLength="19"
            />
          </FullWidthField>
          
          <FormField>
            <FormLabel>Expiry Date</FormLabel>
            <FormInput
              type="text"
              placeholder="MM/YY"
              maxLength="5"
            />
          </FormField>
          
          <FormField>
            <FormLabel>CVV</FormLabel>
            <FormInput
              type="text"
              placeholder="123"
              maxLength="4"
            />
          </FormField>
        </FormGrid>
      )}
    </PaymentSection>
  );

  const renderReview = () => (
    <FormSection>
      <SectionTitle>📋 Order Review</SectionTitle>
      
      <OrderSummary>
        <SummaryTitle>Order Summary</SummaryTitle>
        {cart.map(item => (
          <SummaryItem key={item.id}>
            <SummaryLabel>
              {item.quantity}x {item.name} (from {item.restaurantName})
            </SummaryLabel>
            <SummaryValue>{formatPrice(item.price * item.quantity)}</SummaryValue>
          </SummaryItem>
        ))}
        
        <SummaryItem>
          <SummaryLabel>Subtotal</SummaryLabel>
          <SummaryValue>{formatPrice(calculateSubtotal())}</SummaryValue>
        </SummaryItem>
        
        <SummaryItem>
          <SummaryLabel>Tax (8%)</SummaryLabel>
          <SummaryValue>{formatPrice(calculateTax())}</SummaryValue>
        </SummaryItem>
        
        <SummaryItem>
          <SummaryLabel>Total</SummaryLabel>
          <SummaryValue $highlight>{formatPrice(calculateTotal())}</SummaryValue>
        </SummaryItem>
      </OrderSummary>
      
      <OrderSummary>
        <SummaryTitle>Contact Information</SummaryTitle>
        <SummaryItem>
          <SummaryLabel>Name</SummaryLabel>
          <SummaryValue>{formData.firstName} {formData.lastName}</SummaryValue>
        </SummaryItem>
        <SummaryItem>
          <SummaryLabel>Email</SummaryLabel>
          <SummaryValue>{formData.email}</SummaryValue>
        </SummaryItem>
        <SummaryItem>
          <SummaryLabel>Phone</SummaryLabel>
          <SummaryValue>{formData.phone}</SummaryValue>
        </SummaryItem>
        {formData.address && (
          <SummaryItem>
            <SummaryLabel>Address</SummaryLabel>
            <SummaryValue>
              {formData.address}, {formData.city}, {formData.state} {formData.zipCode}
            </SummaryValue>
          </SummaryItem>
        )}
      </OrderSummary>
    </FormSection>
  );

  const renderCurrentStep = () => {
    switch (currentStep) {
      case 1:
        return renderContactInfo();
      case 2:
        return renderPayment();
      case 3:
        return renderReview();
      default:
        return null;
    }
  };

  return (
    <CheckoutContainer>
      <CheckoutHeader>
        <CheckoutTitle>🚀 Checkout</CheckoutTitle>
        <CheckoutSubtitle>
          Complete your order in {steps.length} simple steps
        </CheckoutSubtitle>
      </CheckoutHeader>

      <CheckoutSteps>
        {steps.map((step, index) => (
          <Step key={step.number}>
            <StepNumber
              $active={currentStep === step.number}
              $completed={currentStep > step.number}
            >
              {currentStep > step.number ? '✓' : step.number}
            </StepNumber>
            <StepLabel
              $active={currentStep === step.number}
              $completed={currentStep > step.number}
            >
              {step.label}
            </StepLabel>
          </Step>
        ))}
      </CheckoutSteps>

      {renderCurrentStep()}

      <ActionButtons>
        {currentStep > 1 && (
          <BackButton onClick={handleBack}>
            ← Back
          </BackButton>
        )}
        
        {currentStep < 3 ? (
          <NextButton
            onClick={handleNext}
            disabled={!isFormValid()}
          >
            Next →
          </NextButton>
        ) : (
          <PlaceOrderButton
            onClick={handlePlaceOrder}
            disabled={!isFormValid() || isProcessing}
          >
            {isProcessing ? 'Processing...' : '🎉 Place Order'}
          </PlaceOrderButton>
        )}
        
        {currentStep === 1 && (
          <BackButton onClick={onClose}>
            Cancel
          </BackButton>
        )}
      </ActionButtons>
    </CheckoutContainer>
  );
};

export default CheckoutFlow;
